import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { FilterProps } from "react-table";

import dayjs from "dayjs-local";
import { useLiveData } from "hooks";
import { Page } from "modules/layout/Page";
import { TableLogs, LogDataSelect } from "components/Table";
import { PauseButton } from "components/PauseButton";
import { FirebaseShard } from "firebaseWrapper";

import { DeviceStatus } from "../components/DeviceStatus";

interface ColumnDetails {
  [key: string]: string;
}

interface LogData {
  timestamp_utc: string;
  log: { msg: string; priority: string; tag: string; timestamp: number }[];
}

export const LogsPage = () => {
  const { siteId, unitId, deviceId } = useParams();
  const [latestData, setLatestData] = useState<LogData[]>([]);
  const [isPaused, setIsPaused] = useState<boolean>(false);

  useLiveData(
    {
      isPaused: isPaused,
      maxDataPoints: 100,
      firebaseShard: FirebaseShard.Tracing,
      firebaseRef: `devices/dataBySite/${siteId}/historyByDevice/${unitId}/${deviceId}/data/log_data`,
      orderByDescending: true,
      onInitialData: setLatestData,
      onIncrementalData: setLatestData,
    },
    [deviceId]
  );

  const data = latestData
    .map((message, outerIndex) =>
      message.log.map(
        (log, innerIndex) =>
          ({
            timestamp_utc: dayjs.utc(message.timestamp_utc).fromNow(),
            timestamp: dayjs.duration(log.timestamp).humanize(),
            priority: log.priority,
            tag: log.tag,
            msg: log.msg,
            key: `${outerIndex}-${innerIndex}`,
          } as ColumnDetails)
      )
    )
    .flat();

  function SelectColumnFilter({
    column: { filterValue, setFilter, preFilteredRows, id },
  }: FilterProps<LogDataSelect>) {
    const options = React.useMemo(() => {
      const options = new Set<string>();
      preFilteredRows.forEach((row) => {
        options.add(row.values[id]);
      });
      return [...Array.from(options.values())];
    }, [id, preFilteredRows]);

    return (
      <select
        value={filterValue}
        onChange={(e) => {
          setFilter(e.target.value || undefined);
        }}
      >
        <option value="">All</option>
        {options.map((option, i) => (
          <option key={i} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  const columns = React.useMemo(
    () => [
      {
        Header: "Log Date",
        accessor: "timestamp_utc",
        Filter: SelectColumnFilter,
        filter: "includes",
      },
      {
        Header: "Since Device Start",
        accessor: "timestamp",
        Filter: SelectColumnFilter,
        filter: "includes",
      },
      {
        Header: "Priority",
        accessor: "priority",
        Filter: SelectColumnFilter,
        filter: "includes",
      },
      {
        Header: "Tag",
        accessor: "tag",
        Filter: SelectColumnFilter,
        filter: "includes",
      },
      {
        Header: "Message",
        accessor: "msg",
        Filter: SelectColumnFilter,
        filter: "includes",
      },
    ],
    []
  );

  return (
    <Page
      titleSupplement={
        <DeviceStatus
          siteId={siteId}
          unitId={unitId}
          deviceId={deviceId}
          className="ml-2"
        />
      }
      titleButtons={
        <>
          <PauseButton
            isPaused={isPaused}
            onClick={() => setIsPaused((state) => !state)}
          />
        </>
      }
    >
      <TableLogs columns={columns} data={data} isPaused={isPaused} />
    </Page>
  );
};
